/**
 * AnchorMarks - API Module
 * Handles all API communication with the backend
 */

import { getAuthBridge, API_BASE } from "@contexts/context-bridge";
import * as state from "@features/state.ts";
import type { User } from "../types/index";

/**
 * Error thrown for non-2xx API responses. Carries the HTTP status and, for
 * 429 responses, the server's Retry-After hint (seconds). Callers should
 * prefer `err.status === 429` over string-matching the message ("rate limit"),
 * which silently missed differently-worded responses.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly retryAfter?: number;

  constructor(
    message: string,
    status: number,
    retryAfterHeader?: string | null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    if (status === 429 && retryAfterHeader) {
      const seconds = Number.parseInt(retryAfterHeader, 10);
      if (Number.isFinite(seconds) && seconds > 0) this.retryAfter = seconds;
    }
  }
}

// Request deduplication: cache pending requests to prevent duplicate API calls
const pendingRequests = new Map<string, Promise<unknown>>();

// Request metadata for cleanup
const requestMetadata = new Map<string, { timestamp: number }>();

// Default timeout for requests (30 seconds)
const DEFAULT_TIMEOUT_MS = 30000;

// Max cache size to prevent memory leaks
const MAX_CACHE_SIZE = 100;

// Cleanup threshold: remove entries older than this (5 minutes)
const CLEANUP_THRESHOLD_MS = 5 * 60 * 1000;

let cleanupIntervalId: ReturnType<typeof setInterval> | null = null;

function cleanupCache(): void {
  const now = Date.now();
  const keysToDelete: string[] = [];

  // Find expired entries
  for (const [key, metadata] of requestMetadata.entries()) {
    if (now - metadata.timestamp > CLEANUP_THRESHOLD_MS) {
      keysToDelete.push(key);
    }
  }

  // If still over limit after expiry cleanup, remove oldest entries
  if (requestMetadata.size > MAX_CACHE_SIZE) {
    const sortedEntries = [...requestMetadata.entries()].sort(
      (a, b) => a[1].timestamp - b[1].timestamp,
    );
    const removeCount = requestMetadata.size - MAX_CACHE_SIZE;
    for (let i = 0; i < removeCount; i++) {
      keysToDelete.push(sortedEntries[i][0]);
    }
  }

  // Remove duplicates and delete
  const uniqueKeys = [...new Set(keysToDelete)];
  for (const key of uniqueKeys) {
    pendingRequests.delete(key);
    requestMetadata.delete(key);
  }
}

function startDedupCleanupInterval(): void {
  if (cleanupIntervalId != null) return;
  cleanupIntervalId = setInterval(cleanupCache, CLEANUP_THRESHOLD_MS);
}

function stopDedupCleanupInterval(): void {
  if (cleanupIntervalId != null) {
    clearInterval(cleanupIntervalId);
    cleanupIntervalId = null;
  }
}

startDedupCleanupInterval();

// Teardown on page unload so the interval does not keep the context alive
function onPageUnload(): void {
  stopDedupCleanupInterval();
}
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", onPageUnload);
  window.addEventListener("beforeunload", onPageUnload);
}

function getStringField(
  value: Record<string, unknown> | null,
  key: string,
): string | undefined {
  const field = value?.[key];
  return typeof field === "string" ? field : undefined;
}

function getErrorMessage(payload: unknown, fallback: string): string {
  if (typeof payload === "string") return payload;
  if (payload && typeof payload === "object") {
    const errorField = (payload as Record<string, unknown>).error;
    if (typeof errorField === "string") return errorField;
  }
  return fallback;
}

/**
 * Generate a cache key for request deduplication
 */
function getRequestKey(endpoint: string, options: RequestInit): string {
  const method = options.method || "GET";
  const body = options.body ? String(options.body) : "";
  return `${method}:${endpoint}:${body}`;
}

/**
 * API Helper with request deduplication and cancellation support
 * @param {string} endpoint - The API endpoint to call.
 * @param {RequestInit} options - Standard fetch options. Can include signal for cancellation.
 * @param {number} timeout - Request timeout in milliseconds (default: 30000)
 * @returns {Promise<T>} - The response data with type safety.
 */
export async function api<T = unknown>(
  endpoint: string,
  options: RequestInit = {},
  timeout: number = DEFAULT_TIMEOUT_MS,
): Promise<T> {
  // For GET requests, check if there's already a pending request
  const isGet = !options.method || options.method === "GET";
  const requestKey = isGet ? getRequestKey(endpoint, options) : null;

  // Return existing promise if request is already in flight (unless cancelled)
  if (requestKey && pendingRequests.has(requestKey)) {
    const existingPromise = pendingRequests.get(requestKey)!;
    // Check if the existing request was cancelled
    if (options.signal && options.signal.aborted) {
      // Create new request if previous was cancelled
      pendingRequests.delete(requestKey);
      requestMetadata.delete(requestKey);
    } else {
      return existingPromise as Promise<T>;
    }
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  // Always send CSRF token for state-changing requests
  const method = (options.method || "GET").toUpperCase();
  let authBridge: ReturnType<typeof getAuthBridge> | null = null;
  let csrfToken: string | null = null;
  try {
    authBridge = getAuthBridge();
    csrfToken = authBridge.getCsrfToken();
  } catch {
    // AuthProvider not yet mounted - fall back to the vanilla state module
    // imported at module scope. This ensures tests that set values on the
    // state module (via import) are reflected here.
    try {
      const legacyState = state as { csrfToken?: string };
      csrfToken = legacyState.csrfToken ?? null;
    } catch {
      csrfToken = null;
    }
  }
  if (["POST", "PUT", "DELETE", "PATCH"].includes(method) && csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }

  // Create AbortController for timeout and cancellation
  const abortController = new AbortController();
  const signal = options.signal || abortController.signal;

  // Set up timeout
  const timeoutId = setTimeout(() => {
    abortController.abort(new Error("Request timeout or cancelled"));
  }, timeout);

  // Create the fetch promise
  const fetchPromise = (async (): Promise<T> => {
    // Compute a fetchable base URL. In Node (tests) undici requires an
    // absolute URL — if API_BASE is a root-relative path, prefix with
    // http://localhost so fetch doesn't throw when tests call api(). In the
    // browser we keep using API_BASE as-is.
    // If tests have mocked global.fetch (vitest/vi.fn), allow relative URLs
    const globalFetch = (globalThis as { fetch?: { mock?: unknown } }).fetch;
    const isFetchMock = !!globalFetch?.mock;

    const baseForFetch =
      typeof window !== "undefined" ||
      /^https?:\/\//.test(API_BASE) ||
      isFetchMock
        ? API_BASE
        : `http://localhost${API_BASE}`;

    try {
      const response = await fetch(`${baseForFetch}${endpoint}`, {
        ...options,
        signal,
        credentials: "include",
        headers: { ...headers, ...(options.headers as Record<string, string>) },
      });

      if (response.status === 401) {
        // If this was not a refresh call, try refresh-token rotation once
        const isRefreshCall =
          endpoint === "/auth/refresh" || endpoint.startsWith("/auth/refresh");
        if (!isRefreshCall) {
          const refreshRes = await fetch(`${baseForFetch}/auth/refresh`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          });
          if (refreshRes.ok) {
            let refreshData: Record<string, unknown> | null = null;
            const refreshCt = refreshRes.headers.get("content-type");
            if (refreshCt?.includes("application/json")) {
              try {
                refreshData = await refreshRes.json();
              } catch {
                refreshData = null;
              }
            }
            const refreshedCsrfToken = getStringField(refreshData, "csrfToken");
            if (refreshedCsrfToken && authBridge)
              authBridge.setCsrfToken(refreshedCsrfToken);

            const refreshedUser =
              refreshData && typeof refreshData.user === "object"
                ? (refreshData.user as User)
                : null;
            if (refreshedUser && authBridge) {
              authBridge.setCurrentUser(refreshedUser);
              authBridge.setIsAuthenticated(true);
            }
            if (!refreshData) {
              if (authBridge) {
                authBridge.setCsrfToken(null);
                authBridge.setCurrentUser(null);
                authBridge.setIsAuthenticated(false);
              }
              const { showAuthScreen } = await import("@features/auth/auth.ts");
              showAuthScreen();
              throw new Error("Session expired");
            }

            // Retry original request once with new cookies
            const retryRes = await fetch(`${baseForFetch}${endpoint}`, {
              ...options,
              signal,
              credentials: "include",
              headers: {
                ...headers,
                ...(options.headers as Record<string, string>),
                ...(refreshedCsrfToken &&
                ["POST", "PUT", "DELETE", "PATCH"].includes(
                  (options.method || "GET").toUpperCase(),
                )
                  ? { "X-CSRF-Token": refreshedCsrfToken }
                  : {}),
              },
            });
            if (retryRes.ok) {
              const ct = retryRes.headers.get("content-type");
              const data = ct?.includes("application/json")
                ? await retryRes.json()
                : await retryRes.text();
              clearTimeout(timeoutId);
              if (requestKey) {
                pendingRequests.delete(requestKey);
                requestMetadata.delete(requestKey);
              }
              return data as T;
            }
            if (retryRes.status === 401) {
              if (authBridge) {
                authBridge.setCsrfToken(null);
                authBridge.setCurrentUser(null);
                authBridge.setIsAuthenticated(false);
              }
              const { showAuthScreen } = await import("@features/auth/auth.ts");
              showAuthScreen();
              throw new Error("Session expired");
            }
            const retryCt = retryRes.headers.get("content-type");
            const retryData = retryCt?.includes("application/json")
              ? await retryRes.json()
              : await retryRes.text();
            const errMsg = getErrorMessage(
              retryData,
              `API Error: ${retryRes.status} ${retryRes.statusText}`,
            );
            throw new ApiError(
              errMsg,
              retryRes.status,
              retryRes.headers.get("Retry-After"),
            );
          }
        }

        // If we get here, either this was a refresh call or refresh didn't succeed.
        if (authBridge) {
          authBridge.setCsrfToken(null);
          authBridge.setCurrentUser(null);
          authBridge.setIsAuthenticated(false);
        }
        const { showAuthScreen } = await import("@features/auth/auth.ts");
        showAuthScreen();
        throw new Error("Session expired");
      }

      // Handle non-JSON responses (e.g., HTML error pages, empty responses)
      const contentType = response.headers.get("content-type");
      const isJson = contentType?.includes("application/json");

      let data: T;
      try {
        if (isJson) {
          data = await response.json();
        } else {
          // For non-JSON responses, try to get text for better error messages
          const text = await response.text();
          throw new Error(
            `Invalid response format (expected JSON, got ${contentType || "unknown"}): ${text.substring(0, 100)}`,
          );
        }
      } catch (err) {
        // If JSON parsing fails or response is not JSON, provide helpful error
        if (err instanceof SyntaxError) {
          throw new Error(
            `Failed to parse JSON response: ${response.status} ${response.statusText}`,
          );
        }
        throw err;
      }

      if (!response.ok) {
        const errorMessage = getErrorMessage(
          data,
          `API Error: ${response.status} ${response.statusText}`,
        );
        // Special handling for CSRF errors
        if (
          errorMessage.toLowerCase().includes("csrf") ||
          errorMessage.toLowerCase().includes("x-csrf-token")
        ) {
          if (authBridge) {
            authBridge.setCsrfToken(null);
            // Optionally trigger re-auth or reload UI
          }
        }
        throw new ApiError(
          errorMessage,
          response.status,
          response.headers.get("Retry-After"),
        );
      }

      return data;
    } catch (err) {
      // Handle abort: rethrow our typed errors, or map AbortError to timeout message
      if (err instanceof Error) {
        if (
          err.message === "Request cancelled" ||
          err.message === "Request timeout or cancelled"
        ) {
          throw err;
        }
        if (err.name === "AbortError") {
          throw new Error("Request timeout or cancelled");
        }
      }
      throw err;
    } finally {
      // Clear timeout
      clearTimeout(timeoutId);
      // Remove from pending requests when done (success or failure)
      if (requestKey) {
        pendingRequests.delete(requestKey);
        requestMetadata.delete(requestKey);
      }
    }
  })();

  // Cache the promise for GET requests
  if (requestKey) {
    pendingRequests.set(requestKey, fetchPromise);
    requestMetadata.set(requestKey, { timestamp: Date.now() });
  }

  // Expose abort method on the promise for convenience
  (fetchPromise as AbortablePromise<T>).abort = () => {
    clearTimeout(timeoutId);
    abortController.abort(new Error("Request cancelled"));
    if (requestKey) {
      pendingRequests.delete(requestKey);
      requestMetadata.delete(requestKey);
    }
  };

  return fetchPromise;
}

/**
 * Create an abort controller for request cancellation
 * Useful for canceling requests when component unmounts or user navigates away
 */
export function createAbortController(): AbortController {
  return new AbortController();
}

/**
 * Cancel a pending request by endpoint and options
 * Note: This only works for GET requests that were deduplicated
 */
export function cancelRequest(
  endpoint: string,
  options: RequestInit = {},
): void {
  const requestKey = getRequestKey(endpoint, options);
  if (requestKey && pendingRequests.has(requestKey)) {
    const promise = pendingRequests.get(requestKey);
    if (
      promise &&
      typeof (promise as AbortablePromise<unknown>).abort === "function"
    ) {
      (promise as AbortablePromise<unknown>).abort!();
    }
    pendingRequests.delete(requestKey);
    requestMetadata.delete(requestKey);
  }
}

/**
 * Teardown GET-dedup cleanup interval (and unregister unload listeners).
 * Call from tests or app lifecycle to avoid the interval running after unmount/exit.
 */
export function teardownApiDedup(): void {
  stopDedupCleanupInterval();
  if (typeof window !== "undefined") {
    window.removeEventListener("pagehide", onPageUnload);
    window.removeEventListener("beforeunload", onPageUnload);
  }
}

export default { api, teardownApiDedup };
